import {
  useMutationCreateUiPreference,
  useMutationUpdateUiPreference,
  useQueryGetAllUiPreferences,
} from "@apis:ui_preferences/ui_preferences";
import { useUser } from "@libs:react-query-auth";
import { get } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";

// Type for user preference with camelCase properties
type UserPreference = {
  selectedBotId?: {
    chat?: string | null;
    docs?: string | null;
    audio?: string | null;
    translation?: string | null;
    analytics?: string | null;
  } | null;
  botsStreamingEnabled?: boolean | null;
};

export type BotFunctionality =
  | "chat"
  | "docs"
  | "audio"
  | "translation"
  | "analytics";

// URL pattern mappings for bot functionality detection
export const URL_TO_FUNCTIONALITY_MAP: Record<string, BotFunctionality> = {
  "/chat": "chat",
  "/docs": "docs",
  "/audio": "audio",
  "/document-translation": "translation",
  "/analytics": "analytics",
} as const;

export const useBotSelection = () => {
  const user = useUser().data;
  const userId = user?.id;
  const location = useLocation();

  const {
    data: uiPreferences,
    isLoading,
    refetch,
  } = useQueryGetAllUiPreferences(true, userId, undefined, {
    enabled: !!userId,
  });

  const createMutation = useMutationCreateUiPreference({
    onSuccess: () => {
      refetch();
    },
  });

  const updateMutation = useMutationUpdateUiPreference({
    onSuccess: () => {
      refetch();
    },
  });

  // Queue pending bot selection during loading
  const pendingSelection = useRef<{
    botId: string;
    functionality?: BotFunctionality;
  } | null>(null);

  // Determine current page functionality based on URL
  const currentFunctionality = useMemo((): BotFunctionality => {
    const pathname = location.pathname;

    // Find matching functionality based on URL patterns
    for (const [urlPattern, functionality] of Object.entries(
      URL_TO_FUNCTIONALITY_MAP,
    )) {
      if (pathname.includes(urlPattern)) {
        return functionality;
      }
    }

    // Default to chat if can't determine
    return "chat";
  }, [location.pathname]);

  const setSelectedBotId = (
    botId: string,
    functionality?: BotFunctionality,
  ) => {
    if (!userId) return;

    // Queue selection if still loading
    if (isLoading) {
      pendingSelection.current = { botId, functionality };
      return;
    }

    const targetFunctionality = functionality || currentFunctionality;
    const existingPreference = uiPreferences?.find(
      (pref) => pref.userId === userId && !pref.botId,
    );

    if (existingPreference) {
      // Update existing preference - ensure it's a user preference
      const userPref = existingPreference.preference as UserPreference;
      const currentSelectedBotId = userPref?.selectedBotId || {};

      updateMutation.mutate({
        id: existingPreference.id,
        preference: {
          ...userPref,
          selectedBotId: {
            ...currentSelectedBotId,
            [targetFunctionality]: botId,
          },
        } as UserPreference,
      });
    } else {
      // Create new preference
      createMutation.mutate({
        userId: userId,
        botId: null,
        preference: {
          selectedBotId: {
            [targetFunctionality]: botId,
          },
        } as UserPreference,
      });
    }
  };

  // Execute pending selection when loading completes
  // It will use when we auto set bot
  useEffect(() => {
    if (!isLoading && pendingSelection.current && userId && uiPreferences) {
      const { botId, functionality } = pendingSelection.current;
      pendingSelection.current = null;

      const targetFunctionality = functionality || currentFunctionality;
      const existingPreference = uiPreferences?.find(
        (pref) => pref.userId === userId && !pref.botId,
      );

      if (existingPreference) {
        const userPref = existingPreference.preference as UserPreference;
        const currentSelectedBotId = userPref?.selectedBotId || {};

        updateMutation.mutate({
          id: existingPreference.id,
          preference: {
            ...userPref,
            selectedBotId: {
              ...currentSelectedBotId,
              [targetFunctionality]: botId,
            },
          } as UserPreference,
        });
      } else {
        createMutation.mutate({
          userId: userId,
          botId: null,
          preference: {
            selectedBotId: {
              [targetFunctionality]: botId,
            },
          } as UserPreference,
        });
      }
    }
  }, [
    isLoading,
    userId,
    uiPreferences,
    currentFunctionality,
    updateMutation,
    createMutation,
  ]);

  const selectedBotId = useMemo(() => {
    if (!userId || !uiPreferences) return null;

    const userPreference = uiPreferences?.find(
      (pref) => pref.userId === userId && !pref.botId,
    );

    if (!userPreference) return null;

    // Type assert since we know this is a user preference (botId is null)
    const userPref = userPreference.preference as UserPreference;
    const selectedBotIds = userPref?.selectedBotId;

    if (!selectedBotIds) return null;

    return get(selectedBotIds, currentFunctionality, null);
  }, [userId, uiPreferences, currentFunctionality]);

  return {
    setSelectedBotId,
    selectedBotId,
  };
};
